import { ActionIcon, Button, Group, NumberInput, Select, Switch, Table, Text, TextInput } from '@mantine/core';
import { IconAdjustments, IconPlus, IconTrash } from '@tabler/icons-react';
import { Fragment, useState } from 'react';
import HelpPopover from '../../HelpPopover/HelpPopover';
import { ARG_TYPES, emptyArg, MAX_ARGS } from '../connectorToolConfig';
import { HELP } from './workspaceHelp';

const labelFor = (label, index) => (index === 0 ? label : `${label} ${index + 1}`);

const SHOPPER_SAMPLES = {
  'identity.email': 'shopper@example.com',
  'identity.phone': '+10000000000',
  'identity.profileId': 'profile-id',
  'identity.externalId': 'customer-id',
  'shopper.lat': '0.00',
  'shopper.lng': '0.00',
  'shopper.city': 'Northtown',
};

const TestValue = ({ row, index, value, onChange }) => {
  const label = labelFor('Test value', index);
  if (row.type === 'boolean')
    return (
      <Switch aria-label={label} checked={value === true} onChange={(event) => onChange(event.currentTarget.checked)} />
    );
  if (row.type === 'enum') {
    const options = row.enumValues
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
    return (
      <Select
        aria-label={label}
        size="xs"
        data={options}
        value={value || null}
        onChange={(next) => onChange(next || '')}
      />
    );
  }
  return (
    <TextInput
      aria-label={label}
      size="xs"
      ff="monospace"
      placeholder={row.pattern || undefined}
      error={row.required && !String(value ?? '').trim()}
      value={value ?? ''}
      onChange={(event) => onChange(event.target.value)}
    />
  );
};

// Declared arguments (filled by the agent) first, then the shopper fields the request reads; every
// row takes a test value for Send. Test values are the workspace's, never part of the saved tool.
// A capability declares no arguments: its request's {{args.*}} come from the platform's own tool,
// so they show as fixed rows (platformNames) with a test value and nothing to add.
const ArgumentsTable = ({
  tool,
  setToolField,
  shopperNames,
  platformNames = [],
  declare = true,
  testValues,
  setTestValue,
  errors = {},
  readOnly,
}) => {
  const [open, setOpen] = useState({});
  const rowErrors = errors.argRows || [];

  const setArg = (index, patch) =>
    setToolField(
      'argRows',
      tool.argRows.map((row, i) => (i === index ? { ...row, ...patch } : row))
    );

  return (
    <>
      <Table verticalSpacing={2} horizontalSpacing={4} fz="xs">
        <Table.Thead>
          <Table.Tr>
            <Table.Th>
              <Group gap={2} wrap="nowrap">
                Name <HelpPopover {...HELP.args} />
              </Group>
            </Table.Th>
            <Table.Th>Type</Table.Th>
            <Table.Th>Req.</Table.Th>
            <Table.Th>
              <Group gap={2} wrap="nowrap">
                Description
                <HelpPopover {...HELP.argDescription} />
              </Group>
            </Table.Th>
            <Table.Th>Test value</Table.Th>
            <Table.Th>Filled by</Table.Th>
            <Table.Th />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {tool.argRows.map((row, index) => {
            const found = rowErrors[index] || {};
            return (
              <Fragment key={index}>
                <Table.Tr>
                  <Table.Td>
                    <TextInput
                      aria-label={labelFor('Argument name', index)}
                      size="xs"
                      ff="monospace"
                      placeholder="city"
                      disabled={readOnly}
                      error={found.name}
                      value={row.name}
                      onChange={(event) => setArg(index, { name: event.target.value })}
                    />
                  </Table.Td>
                  <Table.Td>
                    <Select
                      aria-label={labelFor('Argument type', index)}
                      size="xs"
                      w={92}
                      data={ARG_TYPES}
                      allowDeselect={false}
                      disabled={readOnly}
                      value={row.type}
                      onChange={(value) => setArg(index, { type: value || 'string' })}
                    />
                  </Table.Td>
                  <Table.Td>
                    <Switch
                      aria-label={labelFor('Required', index)}
                      size="xs"
                      disabled={readOnly}
                      checked={row.required}
                      onChange={(event) => setArg(index, { required: event.currentTarget.checked })}
                    />
                  </Table.Td>
                  <Table.Td>
                    <TextInput
                      aria-label={labelFor('Argument description', index)}
                      size="xs"
                      disabled={readOnly}
                      error={found.description}
                      value={row.description}
                      onChange={(event) => setArg(index, { description: event.target.value })}
                    />
                  </Table.Td>
                  <Table.Td>
                    <TestValue
                      row={row}
                      index={index}
                      value={testValues[`args.${row.name.trim()}`]}
                      onChange={(value) => setTestValue(`args.${row.name.trim()}`, value)}
                    />
                  </Table.Td>
                  <Table.Td>Agent</Table.Td>
                  <Table.Td>
                    <Group gap={0} wrap="nowrap">
                      <ActionIcon
                        variant="subtle"
                        size="sm"
                        aria-label={labelFor('Constraints', index)}
                        onClick={() => setOpen((shown) => ({ ...shown, [index]: !shown[index] }))}
                      >
                        <IconAdjustments size={14} />
                      </ActionIcon>
                      <ActionIcon
                        variant="subtle"
                        color="danger"
                        size="sm"
                        disabled={readOnly}
                        aria-label={labelFor('Remove argument', index)}
                        onClick={() =>
                          setToolField(
                            'argRows',
                            tool.argRows.filter((_, i) => i !== index)
                          )
                        }
                      >
                        <IconTrash size={14} />
                      </ActionIcon>
                    </Group>
                  </Table.Td>
                </Table.Tr>
                {open[index] && (
                  <Table.Tr>
                    <Table.Td colSpan={7}>
                      <Group gap="xs" grow>
                        {row.type === 'enum' ? (
                          <TextInput
                            label="Values, comma-separated"
                            size="xs"
                            disabled={readOnly}
                            error={found.enumValues}
                            value={row.enumValues}
                            onChange={(event) => setArg(index, { enumValues: event.target.value })}
                          />
                        ) : (
                          <TextInput
                            label="Pattern"
                            size="xs"
                            ff="monospace"
                            placeholder="^[0-9]{6}$"
                            disabled={readOnly}
                            error={found.pattern}
                            value={row.pattern}
                            onChange={(event) => setArg(index, { pattern: event.target.value })}
                          />
                        )}
                        <NumberInput
                          label="Max length"
                          size="xs"
                          disabled={readOnly}
                          error={found.maxLength}
                          value={row.maxLength === '' ? '' : Number(row.maxLength)}
                          onChange={(value) => setArg(index, { maxLength: value === '' ? '' : String(value) })}
                        />
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                )}
              </Fragment>
            );
          })}
          {[
            ...platformNames.map((name) => ({
              name: `args.${name}`,
              from: "From the platform's own tool",
              by: 'Platform',
            })),
            ...shopperNames.map((name) => ({ name, from: 'From the shopper on a live call', by: 'Shopper' })),
          ].map(({ name, from, by }) => (
            <Table.Tr key={name}>
              <Table.Td ff="monospace">{name}</Table.Td>
              <Table.Td colSpan={3}>
                <Text size="xs" c="dimmed">
                  {from}
                </Text>
              </Table.Td>
              <Table.Td>
                <TextInput
                  aria-label={`Test value ${name}`}
                  size="xs"
                  ff="monospace"
                  placeholder={SHOPPER_SAMPLES[name]}
                  value={testValues[name] ?? ''}
                  onChange={(event) => setTestValue(name, event.target.value)}
                />
              </Table.Td>
              <Table.Td>{by}</Table.Td>
              <Table.Td />
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
      {errors.argCount && (
        <Text size="xs" c="red">
          {errors.argCount}
        </Text>
      )}
      {declare && !readOnly && (
        <Button
          variant="subtle"
          size="compact-xs"
          leftSection={<IconPlus size={12} />}
          disabled={tool.argRows.length >= MAX_ARGS}
          onClick={() => setToolField('argRows', [...tool.argRows, { ...emptyArg(), required: true }])}
          mt={4}
        >
          Add argument
        </Button>
      )}
    </>
  );
};

export default ArgumentsTable;
